import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { useEffect, useMemo, useState } from "react";
import {
  TreeNode,
  createNodesForSteps,
  createNodesForStepsGroupedByState,
} from "~/components/build/Show/pages/steps/list/components/TreeNode";
import { CollapsibleState } from "~/components/build/Show/pages/steps/list/components/TreeItem/CollapsibleState";
import {
  DisplayMode,
  useStateCounts,
  getRelevantSubFiltersForStep,
  filterJobs,
  FilterByOption,
  filterSteps,
} from "~/components/build/Show/lib/useFilterStore";
import { useBuildPreferencesStore } from "~/components/build/Show/lib/useBuildPreferencesStore";
import { CommandJob } from "~/components/build/Show/lib/types";
import useExpandedIds from "~/components/build/Show/lib/useExpandedIds";
import { useSteps } from "~/components/build/Show/lib/useSteps";
import { useQueryClient } from "@tanstack/react-query";
import { topLevelStepsQueryOptions } from "~/components/build/Show/lib/queries/steps";

export interface FlatTreeNode extends TreeNode {
  depth: number;
  collapsibleState: CollapsibleState;
  parentPath: FlatTreeNode[];
  index: number;
  isLastChild: boolean;
}

/**
 * Returns a list of visible nodes based on expanded states and depth.
 */
export function flattenTreeNodes(
  nodes: TreeNode[],
  collapsibleStates: Map<string, CollapsibleState>,
  depth: number,
  filters: FilterByOption[] = [],
  index = 0,
): FlatTreeNode[] {
  const result: FlatTreeNode[] = [];

  for (let nodeIndex = 0; nodeIndex < nodes.length; nodeIndex++) {
    const node = nodes[nodeIndex];
    const defaultCollapsibleState = node.defaultCollapsibleState ?? CollapsibleState.Collapsed;
    const storedState = collapsibleStates.get(node.id);
    const visibleNode = {
      ...node,
      collapsibleState:
        node.childNodes === undefined
          ? CollapsibleState.None
          : storedState !== undefined
            ? storedState
            : defaultCollapsibleState,
      depth,
      parentPath: [],
      index,
      isLastChild: nodeIndex === nodes.length - 1,
    };

    result.push(visibleNode);
    index++;

    if (node.childNodes) {
      const childNodes = node.childNodes(visibleNode.collapsibleState, filters);
      const children = flattenTreeNodes(childNodes, collapsibleStates, depth + 1, filters, index);

      children.forEach((child) => {
        child.parentPath = [visibleNode, ...child.parentPath];
        child.index = index++;
      });

      result.push(...children);
    }
  }

  return result;
}

export function filterStepJobs<T extends CommandJob>(jobs: T[], step, build): T[] {
  const filters = getRelevantSubFiltersForStep(step, build);
  return filterJobs(jobs, filters, build);
}

interface UseVisibleNodesProps {
  /**
   * Display mode to be used
   */
  displayMode?: DisplayMode;

  /**
   * Filters to apply to the list of steps.
   */
  filters?: FilterByOption[];
}

/**
 * Perform query (if necessary) for the top-level nodes of the tree.
 */
const useTreeNodesQuery = (displayMode: DisplayMode = DisplayMode.State) => {
  const { build } = useBuild();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(true);

  const query = useMemo(() => {
    if (displayMode === DisplayMode.Pipeline) {
      return queryClient.fetchQuery(topLevelStepsQueryOptions(build));
    } else if (displayMode === DisplayMode.State) {
      return Promise.resolve();
    }
    throw new Error(`Invalid display mode: ${displayMode}`);
  }, [displayMode]);

  useEffect(() => {
    query.finally(() => {
      setIsLoading(false);
    });
  }, [query]);

  return {
    // Display by state isn't blocked on an initial request so we don't need to wait for it to load.
    isLoading: displayMode === DisplayMode.State ? false : isLoading,
  };
};

/**
 * Based on a filtered list of steps and display mode, return a list of visible tree nodes
 * respecting the expanded state of steps or state groups.
 */
export function useTreeNodes({ displayMode, filters }: UseVisibleNodesProps = {}): {
  isLoading: boolean;
  nodes: FlatTreeNode[];
} {
  const collapsibleStates = useExpandedIds();
  const stateCounts = useStateCounts();
  const { steps, stepJobs } = useSteps();
  const { build } = useBuild();
  const showSkippedSteps = useBuildPreferencesStore((state) => state.showSkippedSteps);

  /**
   * Fetch the initial tree nodes based on the display mode and filters, which in turn updates the steps store.
   */
  const { isLoading } = useTreeNodesQuery(displayMode);

  /**
   * Transform steps from the store into tree nodes based on the display mode.
   */
  const nodes = useMemo(() => {
    if (displayMode === DisplayMode.Pipeline) {
      // Apply filters to the top-level steps, collapsing consecutive wait steps and hiding ignored steps unless showSkippedSteps is on.
      const filteredSteps = filterSteps(steps, filters || [], build, {
        showSkippedSteps,
        keepGroupsWithMatchingChildren: true,
      });

      return createNodesForSteps(build, filteredSteps, filters || []);
    } else if (displayMode === DisplayMode.State) {
      // When filters are active, only show state groups that match the selected filters.
      // When showSkippedSteps is off, exclude the ignored/skipped state group.
      const hasExplicitSkippedFilter = filters?.includes(FilterByOption.Ignored);
      const filteredCounts = Object.fromEntries(
        Object.entries(stateCounts).filter(([state]) => {
          if (!showSkippedSteps && !hasExplicitSkippedFilter && state === FilterByOption.Ignored) {
            return false;
          }
          if (filters && filters.length > 0) {
            return filters.includes(state as FilterByOption);
          }
          return true;
        }),
      );

      return createNodesForStepsGroupedByState(build, filteredCounts);
    }
    throw new Error(`Invalid display mode: ${displayMode}`);
  }, [steps, displayMode, filters, stateCounts, showSkippedSteps, build]);

  /**
   * Flatten the tree nodes into a flat array for virtualization.
   */
  const visible: FlatTreeNode[] = useMemo(() => {
    return flattenTreeNodes(nodes, collapsibleStates, 0, filters);
  }, [nodes, collapsibleStates, filters, steps, stepJobs]);

  // Derive loading state from actual data readiness, not just the query state.
  const effectiveLoading = useMemo((): boolean => {
    switch (displayMode) {
      case DisplayMode.State:
        // Loading until build.statistics is available to populate state counts.
        return build.statistics == null;
      case DisplayMode.Pipeline:
        // Loading until the query completes and steps are hydrated.
        return (
          isLoading ||
          (steps.length === 0 &&
            (build.statistics == null || (build.statistics.stepsCount ?? 0) > 0))
        );
      default:
        return false;
    }
  }, [displayMode, build.statistics, isLoading, steps.length]);

  return { isLoading: effectiveLoading, nodes: visible };
}
